import BaseMap from 'components/BaseMap/BaseMap';
import { usePerms } from 'contexts/PermsContext';
import { resolveGroupDevices } from 'features/Integrations/features/ConnectedDeviceGroups/utils/settings.utils';
import {
  selectLinkingGroupId,
  selectMapRecenterTrigger,
  selectSelectedDeviceId,
  selectSelectedDeviceIndex,
} from 'features/Integrations/state/selectors/deviceSelectors';
import { selectVisibleDeviceTypes } from 'features/Integrations/state/selectors/integrationSelectors';
import {
  DeviceIsDeviceType,
  DeviceIsSystemType,
} from 'features/Integrations/utils/deviceComparison.utils';
import { canViewDeviceType } from 'features/Integrations/utils/integrationVisibility.utils';
import useLocation from 'hooks/useLocation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { IoMdInformationCircleOutline } from 'react-icons/io';
import { Marker, Polygon, Popup } from 'react-leaflet';
import { useSelector } from 'react-redux';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { useFusionGetIntegrationsQuery } from 'services/integrations/integrations.api';
// prefer `useLocation` for effective/user/jurisdiction defaults
import ConnectedDevicesLoadingState from '../../components/ConnectedDevicesLoadingState/ConnectedDevicesLoadingState';
import { isBuoyDevice } from '../Buoy/utils/buoyDevice.utils';
import MapLegendIcon from './assets/MapLegendIcon.svg';
import DeviceFilters from './components/DeviceFilters/DeviceFilters';
import DeviceMarker from './components/DeviceMarker/DeviceMarker';
import FloodChainLayer from './components/FloodChainLayer/FloodChainLayer';

import DeviceMapLegend from './features/components/DeviceMapLegend/DeviceMapLegend';
import { getLocationCoordinates, getMapCoords, resolveGroupMapView } from './utils/deviceMap.utils';

/** The map filter's "flood sim waterway" switch, remembered per browser. Default on. */
const FLOOD_CHAIN_PREF = 'deviceMap.showFloodChain';
const readShowFloodChain = () => {
  try {
    return window.localStorage.getItem(FLOOD_CHAIN_PREF) !== 'off';
  } catch {
    return true;
  }
};
const storeShowFloodChain = (on) => {
  try {
    window.localStorage.setItem(FLOOD_CHAIN_PREF, on ? 'on' : 'off');
  } catch {
    /* a private window or blocked storage — the switch still works for this page */
  }
};

const DeviceMap = ({
  deviceTypeToFilter, // Filter key used by the map filter UI.
  selectedGroup, // Selected group for filter dropdowns (default mode).
  setSelectedGroup, // Setter for selectedGroup (default mode).
  setUseThisDevice = () => {}, // Callback for map marker selection.
  setShowCurrentDevice = () => {}, // Controls whether to show current device details.
  setShowDeviceSettings = () => {}, // Opens device settings from marker popups.
  filtersCompact = false, // Force compact filter UI (widgets/small screens).
  mode = 'default', // "default" uses context devices; "group" uses override list.
  enableFilters = mode !== 'group', // Enable/disable BaseMap filters.
  selectedDeviceOverride = null, // Explicit device to center on (widget-specific).
  devicesOverride = null, // Device list override used in group mode.
  mapBoundsOverride = null, // Explicit bounds for group map framing.
  setShowProvisional = () => {}, // Callback to show provisional data info for USGS.
  usgsMapWidget = false, // Flag to indicate if used within USGS Map Widget
  widget = false, // When true, disables Redux-driven cross-component sync (map centering).
  expandButton = null, // optional JSX element rendered to the left of the Filters button
  disableFilter = false, // Flag to disable the map filter UI
}) => {
  const { data: connectedDevices = [], isFetching: connectedDevicesFetching } =
    useGetConnectedDevicesQuery(); // Fetch devices with node data from the last 24 hours

  // Mike, 2026-08-28: "a toggle to turn it on and off" — hide the flood sim
  // waterway lines from the map filter. Remembered per browser.
  const [showFloodChain, setShowFloodChainState] = useState(readShowFloodChain);
  const setShowFloodChain = (on) => {
    setShowFloodChainState(on);
    storeShowFloodChain(on);
  };
  const {
    jurisdictions = [],
    homeJurisdiction,
    jurisdictionPolygons = [],
    locations,
  } = useLocation();
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();
  const { data: allGroups = [] } = useGetGroupsQuery();
  const selectedDeviceIndex = useSelector(selectSelectedDeviceIndex);
  const selectedDeviceId = useSelector(selectSelectedDeviceId);
  const recenterCounter = useSelector(selectMapRecenterTrigger);
  const selectedGroupId = useSelector(selectLinkingGroupId);
  // Use the hook's effective location as the initial fallback for user position
  const contextDevices = connectedDevices || [];
  const [legendToggle, setLegendToggle] = useState(false); //Used to control the map Legend display

  const isUSGSConnected = useMemo(() => {
    return integrations.some((integration) => integration.system_type === 'USGS');
  }, [integrations]);

  const devices =
    mode === 'group' && Array.isArray(devicesOverride) ? devicesOverride : contextDevices;
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);
  const perms = usePerms();
  const canViewUSGS = perms.testPermission(
    [8, 9, 10, 11, 12, 13],
    ['ViewUSGSMonitoringStationConnections'],
  );

  const [mapCenter, setMapCenter] = useState(getMapCoords(null, null)); // use Fallback coords
  const [mapZoom, setMapZoom] = useState(10); // Default zoom level
  const [mapBounds, setMapBounds] = useState(null); // Map bounds
  const [userPosition, setUserPosition] = useState(() => {
    const [latitude, longitude] = locations.workplaceLocation.coords;
    return getMapCoords(latitude, longitude);
  }); // User's current position fallback

  const getSelectionZoom = (device) => (isBuoyDevice(device) ? 10 : 16);

  function parseCoordinate(value) {
    const num = parseFloat(value);
    // Check if the parsed number is usable (not NaN, Infinity, or etc)
    return Number.isFinite(num) ? num : null;
  }

  const viewableDevices = useMemo(() => {
    let filtered =
      mode === 'group'
        ? devices
        : devices.filter((device) => canViewDeviceType(device, perms, visibleDeviceTypes));
    if (deviceTypeToFilter) {
      if (deviceTypeToFilter === 'stage') {
        filtered = filtered.filter((device) => DeviceIsSystemType(device, 'Flood'));
      }
      if (deviceTypeToFilter === 'manualRoadClosure') {
        filtered = filtered.filter((device) => DeviceIsDeviceType(device, 'Manual Road Closure'));
      }
      if (deviceTypeToFilter === 'technologyBridgeFlashingLights') {
        filtered = filtered.filter((device) =>
          DeviceIsDeviceType(device, 'Technology Bridge_Flashing Lights'),
        );
      }
      if (deviceTypeToFilter === 'technologyBridgeSiren') {
        filtered = filtered.filter((device) =>
          DeviceIsDeviceType(device, 'Technology Bridge_Siren/Giant Voice'),
        );
      }
    }
    return filtered;
  }, [deviceTypeToFilter, devices, mode, perms, visibleDeviceTypes]);
  const hasBuoyDevices = useMemo(
    () => viewableDevices.some((device) => isBuoyDevice(device)),
    [viewableDevices],
  );

  // Determine if there are any devices with valid location data for mapping
  const hasMappableDevices = useMemo(
    () =>
      Array.isArray(viewableDevices) &&
      viewableDevices.some((device) => getLocationCoordinates(device?.location)),
    [viewableDevices],
  );

  // `jurisdictionPolygons` provided by `useLocation` (array of rings)

  // When jurisdictions load, prefer fitting the map to the home jurisdiction bounds
  // (so the initial view matches AlertMapEditor). Fall back to center when bounds
  // are not available.
  useEffect(() => {
    if (!jurisdictions?.length) return;
    if (homeJurisdiction?.bounds) {
      setMapBounds(homeJurisdiction.bounds);
    } else if (homeJurisdiction?.center) {
      setMapCenter(homeJurisdiction.center);
    }
  }, [jurisdictions, homeJurisdiction]);

  useEffect(() => {
    // Get user's current position using Geolocation API (built-in browser API)
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const userCoordinates = [
          parseCoordinate(position.coords.latitude),
          parseCoordinate(position.coords.longitude),
        ];
        if (userCoordinates.every((value) => value !== null)) {
          setUserPosition(userCoordinates);
        }
      });
    }
  }, []);

  const prevDeviceIndexRef = useRef(null);
  const prevSelectedIdRef = useRef(null);
  const deviceMapRef = useRef(null);

  // Center the map whenever selectedDeviceId changes (set by list, graph, or marker clicks).
  // Disabled in widget mode — widgets center via selectedDeviceOverride instead.
  // When recenterCounter increments (graph point click on already-selected device),
  // call map.setView() directly to bypass LocationSetter's dedup guard.
  useEffect(() => {
    if (mode === 'group' || widget) return;
    if (selectedDeviceId == null) return;
    const device = devices.find((d) => d.id === selectedDeviceId);
    if (!device) return;
    const coords = getLocationCoordinates(device?.location);
    if (!coords) return;
    const zoom = getSelectionZoom(device);
    setMapBounds(null);
    setMapCenter(coords);
    setMapZoom(zoom);
    // Force the Leaflet map to pan even if center/zoom haven't changed (consecutive clicks).
    deviceMapRef.current?.setView(coords, zoom);
  }, [selectedDeviceId, recenterCounter, mode]);

  useEffect(() => {
    if (mode === 'group') return;
    const selectedDevice = selectedDeviceOverride || devices[selectedDeviceIndex];
    if (!selectedDevice) return;

    if (selectedDeviceOverride) {
      const selectedId = selectedDevice?.id ?? selectedDevice?.node_id ?? null;
      if (prevSelectedIdRef.current === selectedId) return;
      prevSelectedIdRef.current = selectedId;
    } else {
      // Only update if the selected device index actually changed
      if (prevDeviceIndexRef.current === selectedDeviceIndex) return;
      prevDeviceIndexRef.current = selectedDeviceIndex;
    }

    const deviceCoordinates = getLocationCoordinates(selectedDevice?.location);
    if (deviceCoordinates) {
      setMapBounds(null);
      setMapCenter(deviceCoordinates);
      setMapZoom(getSelectionZoom(selectedDevice));
    }

    const type = selectedDevice?.device_type;
    if (
      type === 'Manual Road Closure' ||
      type === 'Technology Bridge_Flashing Lights' ||
      type === 'Technology Bridge_Siren/Giant Voice'
    ) {
      if (deviceCoordinates) {
        setMapBounds(null);
        setMapCenter(deviceCoordinates);
        setMapZoom(16);
      }
      setShowCurrentDevice(false);
    }
  }, [mode, selectedDeviceIndex, selectedDeviceOverride, setShowCurrentDevice]);

  useEffect(() => {
    if (mode === 'group' && mapBoundsOverride) {
      setMapBounds(mapBoundsOverride);
    }
  }, [mapBoundsOverride, mode]);

  useEffect(() => {
    if (mode === 'modal' && selectedDeviceOverride) {
      setMapBounds(null);
      setMapCenter(getLocationCoordinates(selectedDeviceOverride?.location));
      setMapZoom(getSelectionZoom(selectedDeviceOverride));
    }
  }, [mode, selectedDeviceOverride]);

  // Center map on group devices when a group is selected from the device list.
  const prevLinkingGroupIdRef = useRef(null);
  useEffect(() => {
    if (widget || mode === 'group') return;
    if (selectedGroupId == null) {
      prevLinkingGroupIdRef.current = null; // reset so re-selecting same group fires fresh
      return;
    }
    if (prevLinkingGroupIdRef.current === selectedGroupId) return;
    prevLinkingGroupIdRef.current = selectedGroupId;
    const group = allGroups.find((g) => g.id === selectedGroupId);
    if (!group) return;
    setSelectedGroup?.(group);
    const groupDevices = resolveGroupDevices(connectedDevices, group);
    const mapView = resolveGroupMapView(groupDevices);
    if (!mapView) return;
    if (mapView.type === 'center') {
      setMapCenter(mapView.center);
      setMapZoom(mapView.zoom);
      setMapBounds(null);
    } else {
      setMapBounds(mapView.bounds);
    }
  }, [selectedGroupId, allGroups, connectedDevices, widget, mode]);

  return (
    <>
      <div
        className="overflow-hidden rounded-3 h-100 w-100 position-relative"
        style={{ isolation: 'isolate' }}
      >
        <BaseMap
          mapRef={deviceMapRef}
          mapCenter={mapCenter}
          zoom={mapZoom}
          scrollZoom={true}
          bounds={mapBounds ?? homeJurisdiction?.bounds}
          setMapBounds={setMapBounds}
          setUseThisDevice={setUseThisDevice}
          setShowCurrentDevice={setShowCurrentDevice}
        >
          {enableFilters && mode !== 'group' && disableFilter !== true && (
            <DeviceFilters
              deviceTypeToFilter={deviceTypeToFilter}
              selectedGroup={selectedGroup}
              setSelectedGroup={setSelectedGroup}
              setMapBounds={setMapBounds}
              setUseThisDevice={setUseThisDevice}
              setShowCurrentDevice={setShowCurrentDevice}
              // Allow widgets to force compact filter UI regardless of viewport size.
              forceCompact={filtersCompact}
              expandButton={expandButton}
              showFloodChain={showFloodChain}
              setShowFloodChain={setShowFloodChain}
            />
          )}
          {/* Waterway chains and the flood wave's progress — drawn only when at
              least one chain of Stage devices exists (experiment, 2026-08-28). */}
          {showFloodChain && <FloodChainLayer devices={viewableDevices} />}
          {/* Render normalized jurisdiction polygons (rings) */}
          {jurisdictionPolygons.map((polygon, index) => (
            <Polygon key={index} positions={polygon} color="purple" fillOpacity={0} />
          ))}
          {userPosition && (
            <Marker position={userPosition}>
              <Popup>Your location</Popup>
            </Marker>
          )}
          {viewableDevices.map((device, index) => {
            const coords = getLocationCoordinates(device?.location);
            if (!coords) return null;
            const compositeKey = `${device?.system_type ?? 'unknown'}-${device?.id ?? index}`;
            return (
              <DeviceMarker
                key={compositeKey}
                device={device}
                coords={coords}
                setUseThisDevice={setUseThisDevice}
                setMapCenter={setMapCenter}
                setShowCurrentDevice={setShowCurrentDevice}
                setShowDeviceSettings={setShowDeviceSettings}
                selectedDeviceId={selectedDeviceId}
              />
            );
          })}
        </BaseMap>
        {((isUSGSConnected && canViewUSGS) || hasBuoyDevices) && usgsMapWidget && (
          <div
            style={{
              position: 'absolute',
              top: enableFilters && mode !== 'group' ? 56 : 8,
              right: 8,
              bottom: 8,
              zIndex: 3,
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'flex-end',
            }}
          >
            <div
              className="d-inline-flex flex-row align-items-end justify-content-end gap-0"
              style={{
                height: '100%',
                maxHeight: '100%',
                maxWidth: '100%',
                overflow: 'visible',
                width: 'max-content',
                position: 'relative',
                pointerEvents: 'auto',
              }}
            >
              <div className="d-flex flex-column justify-content-end gap-1">
                {isUSGSConnected && canViewUSGS && (
                  <Button
                    variant="primary"
                    className="connected-devices-icon-button connected-devices-map-info-control text-white p-0 d-grid align-items-center justify-content-center"
                    style={{ width: 40, height: 40 }}
                    onClick={() => setShowProvisional?.(true)}
                    title="View USGS disclaimer"
                  >
                    <IoMdInformationCircleOutline size={25} />
                  </Button>
                )}
                <Button
                  variant="primary"
                  className={`connected-devices-icon-button connected-devices-map-info-control text-white p-0 d-grid align-items-center justify-content-center${legendToggle ? ' connected-devices-control-selected' : ''}`}
                  style={{ width: 40, height: 40 }}
                  onClick={() => setLegendToggle((prev) => !prev)}
                  aria-pressed={legendToggle}
                  title="Toggle map legend"
                >
                  <img src={MapLegendIcon} alt="Map Legend" height={25} width={25} />
                </Button>
              </div>
              {legendToggle && (
                <DeviceMapLegend
                  setLegendToggle={setLegendToggle}
                  showBuoySection={hasBuoyDevices}
                  showUSGSSection={isUSGSConnected && canViewUSGS}
                  style={{
                    height: '100%',
                    maxHeight: '100%',
                    maxWidth: widget ? 'min(300px, calc(100vw - 96px))' : 'min(360px, 100%)',
                    overflow: 'hidden',
                    width: widget ? 'min(300px, calc(100vw - 96px))' : 'min(360px, 100%)',
                    borderBottomRightRadius: 0,
                    borderTopRightRadius: 0,
                    marginLeft: 8,
                  }}
                />
              )}
            </div>
          </div>
        )}
        {connectedDevicesFetching && (
          <div className="position-absolute top-0 start-0 m-2 px-2 py-1 rounded bg-dark bg-opacity-75 text-white z-1 small pe-none">
            <ConnectedDevicesLoadingState message="Loading map devices..." />
          </div>
        )}
        {/* Show overlay ONLY if there are truly no devices in the system, not just filtered out */}
        {!connectedDevicesFetching &&
          Array.isArray(contextDevices) &&
          contextDevices.length === 0 && (
            <div
              className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-white-50 bg-dark bg-opacity-25 z-1 pe-none"
              style={{ pointerEvents: 'none' }}
            >
              No map data available
            </div>
          )}
      </div>
    </>
  );
};

export default DeviceMap;
